import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Stack,
  Alert,
  Autocomplete,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Save,
  Delete,
  PersonAdd,
  PersonRemove,
  AccessTime,
  Work
} from '@mui/icons-material';

const ROLES = [
  'Manager',
  'Assistant Manager',
  'Team Lead',
  'Cashier',
  'Sales Associate',
  'Front Desk',
  'Inventory'
];

export default function ShiftEditDialog({
  open,
  onClose,
  shift,
  employees = [],
  selectedDate,
  defaultStartTime,
  defaultEndTime,
  preselectedEmployee,
  defaultRole,
  onSave,
  onDelete,
  scheduleId
}) {
  const pad2 = (n) => String(n).padStart(2, '0');
  const formatLocalDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
  const formatLocalDateTime = (d) => `${formatLocalDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  const [formData, setFormData] = useState({
    role: '',
    start_time: '09:00',
    end_time: '17:00',
    break_minutes: 30,
    employee_id: null,
    notes: '',
    date: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(shift);
  const isCreate = !isEdit;

  // Initialize form data
  useEffect(() => {
    if (isCreate && selectedDate) {
      setFormData({
        role: defaultRole || '',
        start_time: defaultStartTime || '09:00',
        end_time: defaultEndTime || '17:00',
        break_minutes: 30,
        employee_id: preselectedEmployee?.id || null,
        notes: '',
        date: formatLocalDate(selectedDate)
      });
    } else if (isEdit && shift) {
      const start = new Date(String(shift.start_ts).replace(' ', 'T'));
      const end = new Date(String(shift.end_ts).replace(' ', 'T'));

      setFormData({
        role: shift.role || '',
        start_time: start.toTimeString().slice(0, 5),
        end_time: end.toTimeString().slice(0, 5),
        break_minutes: shift.break_minutes || 30,
        employee_id: shift.employee_id || null,
        notes: shift.notes || '',
        date: formatLocalDate(start)
      });
    }
    setErrors({});
  }, [shift, selectedDate, isCreate, isEdit]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.role) newErrors.role = 'Role is required';
    if (!formData.start_time) newErrors.start_time = 'Start time is required';
    if (!formData.end_time) newErrors.end_time = 'End time is required';
    if (!formData.date) newErrors.date = 'Date is required';

    // Validate time range
    if (formData.start_time && formData.end_time) {
      const startMinutes = timeToMinutes(formData.start_time);
      const endMinutes = timeToMinutes(formData.end_time);

      if (endMinutes <= startMinutes) {
        newErrors.end_time = 'End time must be after start time';
      }
    }

    // Validate break time
    if (formData.break_minutes < 0) {
      newErrors.break_minutes = 'Break time cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const startDateTime = new Date(`${formData.date}T${formData.start_time}:00`);
      const endDateTime = new Date(`${formData.date}T${formData.end_time}:00`);

      // Handle overnight shifts
      if (endDateTime <= startDateTime) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      const shiftData = {
        schedule_id: scheduleId,
        role: formData.role,
        start_ts: formatLocalDateTime(startDateTime),
        end_ts: formatLocalDateTime(endDateTime),
        break_minutes: parseInt(formData.break_minutes) || 0,
        employee_id: formData.employee_id,
        notes: formData.notes || null
      };

      await onSave(shiftData, isEdit ? shift.id : null);
      onClose();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!shift?.id) return;

    if (window.confirm('Are you sure you want to delete this shift?')) {
      setLoading(true);
      try {
        await onDelete(shift.id);
        onClose();
      } catch (error) {
        setErrors({ submit: error.message });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAssignEmployee = (employeeId) => {
    handleChange('employee_id', employeeId);
  };

  const handleUnassignEmployee = () => {
    handleChange('employee_id', null);
  };

  const calculateDuration = () => {
    if (!formData.start_time || !formData.end_time) return 0;

    const startMinutes = timeToMinutes(formData.start_time);
    let endMinutes = timeToMinutes(formData.end_time);

    // Handle overnight shifts
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }

    const totalMinutes = endMinutes - startMinutes - (formData.break_minutes || 0);
    return Math.max(0, totalMinutes / 60);
  };

  const assignedEmployee = employees.find(emp => emp.id === formData.employee_id);
  const availableEmployees = employees.filter(emp =>
    emp.role === formData.role || !formData.role
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: 500 }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Work />
          <Typography variant="h6">
            {isCreate ? 'Create New Shift' : 'Edit Shift'}
          </Typography>
          {formData.role && (
            <Chip label={formData.role} size="small" color="primary" />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Basic Shift Info */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Shift Details
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <FormControl fullWidth error={!!errors.role}>
                    <InputLabel>Role/Position</InputLabel>
                    <Select
                      value={formData.role}
                      onChange={(e) => handleChange('role', e.target.value)}
                      label="Role/Position"
                    >
                      {ROLES.map(role => (
                        <MenuItem key={role} value={role}>{role}</MenuItem>
                      ))}
                    </Select>
                    {errors.role && (
                      <Typography variant="caption" color="error">
                        {errors.role}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    type="date"
                    label="Date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    error={!!errors.date}
                    helperText={errors.date}
                    fullWidth
                  />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    type="time"
                    label="Start Time"
                    value={formData.start_time}
                    onChange={(e) => handleChange('start_time', e.target.value)}
                    error={!!errors.start_time}
                    helperText={errors.start_time}
                    fullWidth
                  />
                  <TextField
                    type="time"
                    label="End Time"
                    value={formData.end_time}
                    onChange={(e) => handleChange('end_time', e.target.value)}
                    error={!!errors.end_time}
                    helperText={errors.end_time}
                    fullWidth
                  />
                  <TextField
                    type="number"
                    label="Break (min)"
                    value={formData.break_minutes}
                    onChange={(e) => handleChange('break_minutes', e.target.value)}
                    error={!!errors.break_minutes}
                    helperText={errors.break_minutes}
                    inputProps={{ min: 0, max: 120 }}
                    sx={{ minWidth: 120 }}
                  />
                </Stack>

                {/* Duration Display */}
                <Box sx={{
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <AccessTime sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2">
                    Duration: <strong>{calculateDuration().toFixed(1)} hours</strong>
                    {formData.break_minutes > 0 && (
                      <span> (includes {formData.break_minutes}min break)</span>
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Employee Assignment */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Employee Assignment
              </Typography>

              {assignedEmployee ? (
                <Box sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#f8f9fa'
                }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {assignedEmployee.first_name} {assignedEmployee.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {assignedEmployee.role}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<PersonRemove />}
                      onClick={handleUnassignEmployee}
                      size="small"
                    >
                      Unassign
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <Autocomplete
                    options={availableEmployees}
                    getOptionLabel={(emp) => `${emp.first_name} ${emp.last_name} (${emp.role})`}
                    value={null}
                    onChange={(_, employee) => employee && handleAssignEmployee(employee.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assign Employee"
                        placeholder="Search employees..."
                      />
                    )}
                    renderOption={(props, employee) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">
                            {employee.first_name} {employee.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {employee.role}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Leave unassigned to create an open shift
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Notes */}
            <TextField
              label="Notes (Optional)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any additional notes about this shift..."
              fullWidth
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>

        {isEdit && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
        )}

        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : (isCreate ? 'Create Shift' : 'Save Changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
